import React, { createContext, useState, useCallback, PropsWithChildren } from 'react';
import { noop } from '../utility/generic';
import { MarkerType } from './markerType';

export interface SubscribeReturn {
  unsubscribe: () => void;
  getMarker: () => MarkerType;
}

export interface TimelineMarkersContextValue {
  markers: MarkerType[];
  subscribeMarker: (m: MarkerType) => SubscribeReturn;
  updateMarker: (upd: MarkerType) => void;
}

const defaultContextState: TimelineMarkersContextValue = {
  markers: [],
  subscribeMarker: () => {
    console.warn('default subscribe marker used');
    return {
      unsubscribe: noop,
      getMarker: noop,
    } as SubscribeReturn;
  },
  updateMarker: () => {
    console.warn('default subscribe marker used');
    return noop;
  },
};

const TimelineMarkersContext = createContext<TimelineMarkersContextValue>(defaultContextState);

let _id = 0;
const createId = () => {
  _id += 1;
  return _id + 1;
};

export function TimelineMarkersProvider(props: PropsWithChildren) {
  const [markers, setMarkers] = useState<MarkerType[]>([]);

  const handleSubscribeToMarker = useCallback((newMarker: MarkerType): SubscribeReturn => {
    const markerWithId = {
      ...newMarker,
      id: createId(),
    };

    setMarkers((state) => [...state, markerWithId]);

    return {
      unsubscribe: () => {
        setMarkers((state) => state.filter((marker) => marker.id !== markerWithId.id));
      },
      getMarker: () => markerWithId,
    };
  }, []);

  const handleUpdateMarker = useCallback((updateMarker: MarkerType) => {
    setMarkers((state) => {
      const markerIndex = state.findIndex((marker) => marker.id === updateMarker.id);
      if (markerIndex < 0) return state;
      return [...state.slice(0, markerIndex), updateMarker, ...state.slice(markerIndex + 1)];
    });
  }, []);

  const value: TimelineMarkersContextValue = {
    markers,
    subscribeMarker: handleSubscribeToMarker,
    updateMarker: handleUpdateMarker,
  };

  return (
    <TimelineMarkersContext.Provider value={value}>
      {props.children}
    </TimelineMarkersContext.Provider>
  );
}

TimelineMarkersProvider.displayName = 'TimelineMarkersProvider';

export const TimelineMarkersConsumer = TimelineMarkersContext.Consumer;
